// src/components/CustomerPortalManager.js
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  Alert,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ContentCopy,
  Send,
  Refresh,
  VpnKey,
  CheckCircle,
  Cancel,  Mail,
} from "@mui/icons-material";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";

// Generate secure random password
const generatePassword = () => {
  const length = 12;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

function CustomerPortalManager({ customer, customerId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [portalEnabled, setPortalEnabled] = useState(customer.portalEnabled || false);
  const [portalEmail, setPortalEmail] = useState(
    customer.portalEmail || customer.contactEmail || customer.email || ""
  );
  // We will no longer store the password on the customer document for security reasons.
  const [portalPassword, setPortalPassword] = useState(customer.portalPassword || "");

  const handleTogglePortal = async (enabled) => {
    setLoading(true);
    setMessage(null);

    try {
      const emailToUse = portalEmail || customer.contactEmail || customer.email || "";

      if (enabled && !emailToUse) {
        throw new Error("Customer email is required. Please add an email address to the customer profile first.");
      }

      if (enabled) {
        // Call the Cloud Function to create the user
        const createPortalUser = httpsCallable(functions, 'createPortalUser');
        const result = await createPortalUser({ 
          customerId: customerId,
          email: emailToUse,
          displayName: customer.name 
        });

        // The function returns the generated password
        setPortalPassword(result.data.password);
        setMessage({ type: "success", text: `Portal user created! Password: ${result.data.password}` });

      } else {
        // Call a function to disable the user
        const disablePortalUser = httpsCallable(functions, 'disablePortalUser');
        await disablePortalUser({ customerId: customerId });
        setMessage({ type: "info", text: "Portal access disabled. The user can no longer sign in." });
      }

      // Update the local state
      setPortalEmail(emailToUse);
      
      setPortalEnabled(enabled);
      setMessage({ 
        type: "success", 
        text: enabled ? "Portal access enabled!" : "Portal access disabled" 
      });

      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error updating portal access:", err);
      setMessage({ type: "error", text: "Failed to update portal access" });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePassword = async () => {
    if (!window.confirm("This will generate a new password and reset the user's existing one. Are you sure?")) {
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const resetPortalPassword = httpsCallable(functions, 'resetPortalPassword');
      const result = await resetPortalPassword({ customerId });
      setPortalPassword(result.data.newPassword);
      setMessage({ type: "success", text: `New password generated: ${result.data.newPassword}` });
    } catch (err) {
      console.error("Error resetting password:", err);
      setMessage({ type: "error", text: "Failed to reset password." });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(portalPassword);
    setMessage({ type: "info", text: "Password copied to clipboard!" });
    setTimeout(() => setMessage(null), 2000);
  };

  const handleCopyLoginDetails = () => {
    const details = `HumberTrack Customer Portal Login
Email: ${portalEmail}
Password: ${portalPassword}
Portal: ${window.location.origin}`;
    
    navigator.clipboard.writeText(details);
    setMessage({ type: "info", text: "Login details copied to clipboard!" });
    setTimeout(() => setMessage(null), 2000);
  };

  const handleSendInvite = async () => {
    setLoading(true);
    setMessage(null);

    if (!portalPassword) {
      setMessage({ type: "error", text: "Please generate a password before sending an invite." });
      setLoading(false);
      return;
    }

    try {
      const sendPortalInvite = httpsCallable(functions, 'sendPortalInvite');
      await sendPortalInvite({
        email: portalEmail,
        password: portalPassword,
        customerName: customer.name,
      });
      setMessage({ type: "success", text: "Portal invite email sent successfully!" });
      // Log the invite for now
      console.log("Portal Invite Details:", {
        customerName: customer.name,
        email: portalEmail,
        password: portalPassword,
        portalUrl: window.location.origin,
      });
    } catch (err) {
      console.error("Error sending invite:", err);
      setMessage({ type: "error", text: "Failed to send invite email" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ bgcolor: "background.paper" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🌐 Customer Portal Access
          </Typography>
          <Chip
            icon={portalEnabled ? <CheckCircle /> : <Cancel />}
            label={portalEnabled ? "Enabled" : "Disabled"}
            color={portalEnabled ? "success" : "default"}
            size="small"
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Message Alert */}
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {/* Enable/Disable Switch */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Portal Access
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Allow customer to login and manage their services
              </Typography>
            </Box>
            <Switch
              checked={portalEnabled}
              onChange={(e) => handleTogglePortal(e.target.checked)}
              disabled={loading}
            />
          </Stack>
        </Box>

        {/* Credentials Section (shown when enabled) */}
        {portalEnabled && (
          <Box>
            <Divider sx={{ mb: 3 }} />

            {/* Portal Email */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Portal Email
              </Typography>
              <TextField
                fullWidth
                value={portalEmail}
                onChange={(e) => setPortalEmail(e.target.value)}
                placeholder="customer@email.com"
                disabled={loading}
                size="small"
              />
            </Box>

            {/* Portal Password */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Portal Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                value={portalPassword}
                onChange={(e) => setPortalPassword(e.target.value)}
                placeholder="Enter password"
                disabled={loading}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide" : "Show"}>
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy password">
                        <IconButton
                          size="small"
                          onClick={handleCopyPassword}
                          disabled={!portalPassword}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VpnKey />}
                onClick={handleGeneratePassword}
                disabled={loading}
              >
                Generate
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyLoginDetails}
                disabled={loading || !portalPassword}
              >
                Copy Details
              </Button>
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Send Invite */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Send Invite Email
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                Customer will receive login instructions and credentials
              </Typography>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? <CircularProgress size={16} /> : <Mail />}
                onClick={handleSendInvite}
                disabled={loading || !portalPassword}
                sx={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                  },
                }}
              >
                Send Portal Invite
              </Button>
            </Box>

            {/* Portal Stats (Optional) */}
            <Box sx={{ mt: 3, p: 2, bgcolor: "rgba(59,130,246,0.05)", borderRadius: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Portal URL: {window.location.origin}
                </Typography>
                {customer.portalLastLogin && (
                  <Typography variant="caption" color="text.secondary">
                    Last Login: {new Date(customer.portalLastLogin.seconds * 1000).toLocaleString()}
                  </Typography>
                )}
                {customer.portalUpdatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Updated: {new Date(customer.portalUpdatedAt.seconds * 1000).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {/* Disabled State Info */}
        {!portalEnabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Enable portal access to allow this customer to login and self-service their jobs online.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default CustomerPortalManager;